import * as React from 'react';
import * as _ from 'underscore';

import { ButtonType, ButtonWidget, FlyoutListMenuWidget,  MenuButtonWidget, TabStyles } from '@tableau/widgets';

import { Messages } from '../../../codegen/Localize';
import { BuyLicensesTelemetryEvent, TelemetryEvents } from '../../../common/telemetryEvents/TelemetryEvents';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { UserQuotaIncrease } from '../../../ts/ServerApi/requests/ExpandUserQuotaRequest';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { LicensesService } from '../../../ts/services/LicensesService';
import { ServerService } from '../../../ts/services/ServerService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../../ts/services/SiteAuthenticationSettingsService';
import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { ConnectedExplorer } from '../Explorer/ConnectedExplorer';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { LicenseInfoBar } from '../Header/LicenseInfoBar';
import { SpaceHeaderWithTotalItemsCount } from '../Header/SpaceHeaderWithTotalItemsCount';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { BuyModal } from './BuyLicenseDialog';
import { SiteUserAuthenticationProviderContext } from './UserAuthenticationProviderContext';
import { Telemetry } from '../../../commonInfra/services/Telemetry';

export interface AllSiteUsersSpaceOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
}

export interface AllSiteUsersAngularProps {
  readonly licensesService: LicensesService;
  readonly siteAuthSettingsService: SiteAuthenticationSettingsService;
  readonly serverService: ServerService;
}

export interface AllSiteUsersSpaceStateProps {
  readonly isBusyFetchingItems: boolean;
  readonly isSessionMultiSite: boolean;
  readonly numberOfFetchedUsers: number;
  readonly isSessionServerAdmin: boolean;
  readonly canSessionUserManageUsers: boolean;
}

export interface AllSiteUsersSpaceDispatchProps {
  readonly requestSpaceContents: () => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
  readonly addSiteUsers: () => void;
}

export type AllSiteUsersSpaceProps = AllSiteUsersSpaceOwnProps & AllSiteUsersAngularProps & AllSiteUsersSpaceStateProps & AllSiteUsersSpaceDispatchProps;

type BuyLicensesDisplayType  = 'DEFAULT' | 'ADDMENU';

export interface AllSiteUsersSpaceState {
  readonly displayLicensingBar: boolean;
  readonly hasSiteLicensingInfo: boolean;
  readonly renderChildren: boolean;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
  readonly buyLicensesEnabled: boolean;
  readonly buyLicensesDisplayType: BuyLicensesDisplayType;
  readonly displayBuyModal: boolean;
}

export class AllSiteUsersSpace extends React.PureComponent<AllSiteUsersSpaceProps, AllSiteUsersSpaceState> {
  public constructor(props: AllSiteUsersSpaceProps) {
    super(props);
    this.state = { displayLicensingBar: undefined, hasSiteLicensingInfo: false, renderChildren: false, siteAuthenticationSettings: undefined, buyLicensesEnabled: false, buyLicensesDisplayType: undefined, displayBuyModal: false};
  }

  public componentDidMount() {
    this.props.updateBreadcrumbs([getSiteUsersSpaceBreadcrumb(this.props.urls, this.props.isSessionMultiSite)]);
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.props.isSessionMultiSite ? Messages.siteUsers() : Messages.users() });
    this.props.requestSpaceContents();
    this.updateSiteLicenseConsumptionInfo();
    this.getSiteAuthenticationSettings();
  }

  public componentDidUpdate(prevProps: AllSiteUsersSpaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.props.requestSpaceContents();
    }

    if (!this.state.renderChildren && !_.isUndefined(this.state.displayLicensingBar) && !_.isUndefined(this.state.siteAuthenticationSettings)) {
      this.setState({renderChildren: true});
    }
  }

  public render(): JSX.Element {
    if (!this.state.renderChildren) {
      return null;
    }
    return(
      <div className={ SpaceContainerStyles.SpaceContainer }>
        <SpaceHeaderWithTotalItemsCount
          onClick={this.onSpaceHeaderClick}
          label={this.props.isSessionMultiSite ? Messages.siteUsers() : Messages.users()}
          testId={'site-users'}
          count={this.props.numberOfFetchedUsers}
          countUpToDate={!this.props.isBusyFetchingItems}
          licenseInfoBar={this.licenseBar}
        />
        <div className={ SpaceContainerStyles.SubplaceArea }>
          <SiteUserAuthenticationProviderContext.Provider value={this.authenticationProvider}>
            <ConnectedExplorer
              angularContext={this.props.angularContext}
              supportedDisplayModes={['list']}
              toolbarProps={{
                supportFilterPanel: true,
                toolbarVisibility: 'enabled',
                toolbarContentSpecificControls: { beginning: this.addUserButtonOrMenu() }
              }}
              emptyStateProps={{
                primaryMessage: Messages.usersSiteEmptyMessage()
              }}
            />
          </SiteUserAuthenticationProviderContext.Provider>
        </div>

        {this.state.displayBuyModal &&
        <BuyModal onClose={this.closeBuyModal} server={this.props.serverService} reportTelemetry={this.submissionTelemetryEvent}></BuyModal>}
      </div>
    );
  }

  private authenticationProvider = (user: ISiteUserInfo) => {
    const provider = this.props.siteAuthSettingsService.findProvider(this.state.siteAuthenticationSettings, user.authenticationProviderId);
    if (provider) {
      return {
        name: this.props.siteAuthSettingsService.getProviderDisplayName(provider),
        isEnabled: provider.isEnabled
      };
    } else {
      return {
        name: Messages.unspecified(),
        isEnabled: false
      };
    }
  }

  private getSiteAuthenticationSettings = async () => {
    const siteAuthenticationSettings = await this.props.siteAuthSettingsService.getSiteAuthenticationSettings();
    this.setState({siteAuthenticationSettings});
  }

  private updateSiteLicenseConsumptionInfo = async () => {
    const hasSiteLicensingInfo = await this.props.licensesService.hasSiteLicensingConsumptionInfo();
    const showLicenseInfoBarForSingleSiteServer = !this.props.isSessionMultiSite
      && this.props.isSessionServerAdmin;
    const displayLicensingBar = hasSiteLicensingInfo || showLicenseInfoBarForSingleSiteServer;
    const buyLicensesEnabled = this.props.serverService.isBuyLicensesEnabled();
    const buyLicensesDisplayType: BuyLicensesDisplayType = this.props.serverService.getBuyLicensesDisplayType() as BuyLicensesDisplayType;

    this.setState({
      hasSiteLicensingInfo,
      displayLicensingBar,
      buyLicensesEnabled,
      buyLicensesDisplayType,
    });
  }

  private licenseBar = (): JSX.Element => {
    return (
      <div style={{display: 'flex'}}>
        {this.state.displayLicensingBar && (
          <LicenseInfoBar
            hasSiteLicensingInfo={this.state.hasSiteLicensingInfo}
            licensesService={this.props.licensesService}
          />
        )}
        {this.state.buyLicensesEnabled && this.state.buyLicensesDisplayType === 'DEFAULT' && (
          <>
          <span className={ExplorerStyles.ToolbarButtonDivider} style={{marginTop: TabStyles.Spacing.SmallSpace, marginLeft: TabStyles.Spacing.LargeSpace, marginRight: TabStyles.Spacing.MediumSpace}} />
          <div style={{paddingTop: '2px'}}>
            <BuyLicensesButton
                handleClick={this.buyLicense}
            />
          </div>
          </>
        )}
      </div>
    );
  }

  private addUserButtonOrMenu = () => {
    if (!this.props.canSessionUserManageUsers) {
        return null;
    }
    if(!this.state.buyLicensesEnabled){
        return this.addUserButton();
    }
    if(this.state.buyLicensesDisplayType === 'ADDMENU'){
        Telemetry.sendEvent<BuyLicensesTelemetryEvent>({
            type: TelemetryEvents.BuyLicenses,
            action: 'buy-licenses-button-2-displayed',
        });
        return (
        <AddButtonMenu
            children={Messages.add()}
            addSiteUsers={this.props.addSiteUsers}
            buyLicense={this.buyLicense}/>
        );
    }
    else{
        Telemetry.sendEvent<BuyLicensesTelemetryEvent>({
            type: TelemetryEvents.BuyLicenses,
            action: 'buy-licenses-button-1-displayed',
        });
        return this.addUserButton();
    }
  }

  private addUserButton = () => {
    if (this.props.canSessionUserManageUsers) {
      const handleClick = () => this.props.addSiteUsers();
      return (
        <ButtonWidget
          children={Messages.addUsers()}
          buttonType={ButtonType.Outline}
          handleClick={handleClick}
          tb-test-id={'action-add-users'}
          testId={'action-add-users'}/>
      );
    }
    return null;
  }

  private buyLicense = () => {
    const action = this.getTelemetryActionForBuyButtonClick();
    Telemetry.sendEvent<BuyLicensesTelemetryEvent>({
      type: TelemetryEvents.BuyLicenses,
      action: action,
    });
    this.openBuyModal();
  }

  private getTelemetryActionForBuyButtonClick = (): BuyLicensesTelemetryEvent['action'] => {

    switch(this.state.buyLicensesDisplayType){
        case 'ADDMENU':
            return 'buy-licenses-button-2-clicked';
        default:
            return 'buy-licenses-button-1-clicked';
    }
  }

  private submissionTelemetryEvent = (orderQuantities: UserQuotaIncrease): void => {
    let action: BuyLicensesTelemetryEvent['action'] = 'buy-licenses-button-1-submit';
    if(this.state.buyLicensesDisplayType === 'ADDMENU'){
        action = 'buy-licenses-button-2-submit';
    }
    Telemetry.sendEvent<BuyLicensesTelemetryEvent>({
        type: TelemetryEvents.BuyLicenses,
        action: action,
        ...orderQuantities
    });
  }

  private openBuyModal = (): void => {
    this.setState({ displayBuyModal: true, });
  }

  private closeBuyModal = (): void => {
    this.setState({ displayBuyModal: false, });
  }

  private onSpaceHeaderClick = () => {
    this.props.requestSpaceContents();
  }

}

interface AddButtonMenuProps {
    readonly children: string;
    readonly addSiteUsers: () => void;
    readonly buyLicense: () => void;
}

const AddButtonMenu: React.FC<AddButtonMenuProps> = (props: AddButtonMenuProps) => {
    const [isOpen, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const handleSelect = (value: string) => {
        switch (value) {
          case Messages.addUsers(): {
            props.addSiteUsers();
            break;
          }
          case Messages.buyLicenses(): {
            props.buyLicense();
            break;
          }
        }
        setOpen(false);
      };
    return <>
    <MenuButtonWidget
        aria-controls={'id'}
        tb-test-id={'action-add-users-or-licenses'}
        testId={'action-add-users-or-licenses'}
        isOpen={isOpen}
        ref={setAnchorEl}
        onClick={() => setOpen(!isOpen)}
        children={props.children}
        />
        {isOpen &&
            <FlyoutListMenuWidget
            menuStyle={{ fontWeight: 'normal' }}
            allowAutoPosition
            anchorEl={anchorEl!}
            menuItemInfos={[
                { displayValue: Messages.addUsers(), value: Messages.addUsers() },
                { displayValue: Messages.buyLicenses(), value: Messages.buyLicenses() },
            ]}
            onSelect={handleSelect}
            requestCloseAll={() => setOpen(false)}
            testId="action-add-users-or-licenses-flyout"
            toggleEl={anchorEl!}
            />
        }
    </>;
};

interface BuyLicensesButtonProps {
  readonly handleClick: () => void;
}
const BuyLicensesButton: React.FC<BuyLicensesButtonProps> = (props: BuyLicensesButtonProps) => {

    return (
      <ButtonWidget
        children={Messages.buyLicenses()}
        buttonType={ButtonType.LowEmphasis}
        handleClick={props.handleClick}
        tb-test-id={'buy-licenses'}
        testId={'buy-licenses'} />
    );
};

export function getSiteUsersSpaceBreadcrumb(urls: SpacesSiteUrls, multiSite: boolean): BreadcrumbStateInfo {
  return {
    id: urls && urls.usersUrl(),
    text: multiSite ? Messages.allSiteUsers() : Messages.allUsers(),
    href: urls && urls.usersUrl(),
    testId: 'site-users-link'
  };
}
